<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-account-clock-outline me-2"></i>Staff Availability</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="load" :disabled="loading"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
      </div>
      <div class="hms-muted-sm mb-3" v-if="asOf">As of {{ formatTime(asOf) }} · "on duty" = a shift scheduled today; "on leave" = approved leave covering today.</div>

      <!-- Summary -->
      <div class="hms-stat-row mb-3">
        <div class="hms-stat"><div class="hms-stat-num">{{ summary.total || 0 }}</div><div class="hms-stat-lbl">Active staff</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-text-success">{{ summary.on_duty || 0 }}</div><div class="hms-stat-lbl">On duty now</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-text-warning">{{ summary.on_leave || 0 }}</div><div class="hms-stat-lbl">On leave</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-muted">{{ summary.off_duty || 0 }}</div><div class="hms-stat-lbl">Off duty</div></div>
      </div>

      <!-- Filters -->
      <div class="d-flex flex-wrap gap-2 mb-3">
        <select class="hms-input hms-filter" v-model="cadreFilter">
          <option value="">All cadres</option>
          <option v-for="c in cadres" :key="c" :value="c">{{ c }}</option>
        </select>
        <select class="hms-input hms-filter" v-model="statusFilter">
          <option value="">All statuses</option>
          <option value="on_duty">On duty</option>
          <option value="on_leave">On leave</option>
          <option value="off_duty">Off duty</option>
        </select>
        <div class="hms-search-wrap">
          <i class="mdi mdi-magnify"></i>
          <input class="hms-search" v-model="search" placeholder="Search name…" />
        </div>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else class="hms-card">
        <div class="hms-muted mb-2">{{ filtered.length }} of {{ staff.length }} staff</div>
        <div style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Staff</th><th>Cadre</th><th>Speciality / Type</th><th>Phone</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>
              <tr v-for="s in filtered" :key="s.staff_id">
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <div class="hms-avatar"><img v-if="photoUrl(s)" :src="photoUrl(s)" class="hms-avatar-img" /><span v-else>{{ initials(s) }}</span></div>
                    <div class="fw-semibold">{{ s.first_name }} {{ s.last_name }}</div>
                  </div>
                </td>
                <td>{{ s.cadre_name || '—' }}</td>
                <td class="hms-muted-sm">{{ s.specialization || cap(s.employment_type) || '—' }}</td>
                <td class="hms-muted-sm">{{ s.phone || '—' }}</td>
                <td><span class="hms-badge" :class="badge(s.availability)"><i class="mdi" :class="icon(s.availability)"></i> {{ label(s.availability) }}</span></td>
                <td class="hms-muted-sm">
                  <span v-if="s.availability==='on_duty'">{{ cap(s.shift_type) }}<span v-if="s.shift_start"> · {{ s.shift_start }}–{{ s.shift_end }}</span><span v-if="s.department"> · {{ s.department }}</span></span>
                  <span v-else-if="s.availability==='on_leave'">{{ cap(s.leave_type) }} leave until {{ formatDate(s.leave_until) }}</span>
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!filtered.length" class="hms-empty"><i class="mdi mdi-account-off-outline"></i> No staff match the filters.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms, base } from "@/api";

export default {
  name: "StaffAvailability",
  components: { Navigation },
  data() {
    return {
      loading: true, staff: [], summary: {}, asOf: "",
      cadreFilter: "", statusFilter: "", search: ""
    };
  },
  computed: {
    cadres() {
      var set = {};
      this.staff.forEach(function(s){ if (s.cadre_name) set[s.cadre_name] = 1; });
      return Object.keys(set).sort();
    },
    filtered() {
      var q = (this.search || "").trim().toLowerCase();
      return this.staff.filter(function(s){
        if (this.cadreFilter && s.cadre_name !== this.cadreFilter) return false;
        if (this.statusFilter && s.availability !== this.statusFilter) return false;
        if (q) {
          var hay = ((s.first_name||"") + " " + (s.last_name||"")).toLowerCase();
          if (hay.indexOf(q) === -1) return false;
        }
        return true;
      }.bind(this));
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        var r = await executeJson({ function: "getStaffAvailability" }, hms);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          this.staff = d.staff || [];
          this.summary = d.summary || {};
          this.asOf = d.as_of || "";
        }
      } catch (e) { /* ignore */ }
      this.loading = false;
    },
    initials(s) { return (((s.first_name||"")[0]||"") + ((s.last_name||"")[0]||"")).toUpperCase() || "?"; },
    cap(s) { return s ? (s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g,' ')) : ""; },
    photoUrl(s) {
      var p = s.profile_photo; if (!p) return "";
      if (/^https?:\/\//i.test(p)) return p;
      return (base || "").replace(/\/$/,"") + "/" + String(p).replace(/^\//,"");
    },
    badge(a) { return { on_duty:"hms-badge-success", on_leave:"hms-badge-warning", off_duty:"hms-badge-neutral" }[a] || "hms-badge-neutral"; },
    icon(a) { return { on_duty:"mdi-check-circle", on_leave:"mdi-airplane", off_duty:"mdi-sleep" }[a] || "mdi-help-circle"; },
    label(a) { return { on_duty:"On duty", on_leave:"On leave", off_duty:"Off duty" }[a] || a; },
    formatDate(d) { if (!d) return "—"; var x = new Date(d); return isNaN(x) ? d : x.toLocaleDateString(undefined,{day:'2-digit',month:'short'}); },
    formatTime(d) { if (!d) return ""; var x = new Date(d); return isNaN(x) ? d : x.toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 16px 40px; }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; }
.hms-back:hover { color: var(--hms-primary); }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text-success { color: var(--hms-success); }
.hms-text-warning { color: #b8860b; }
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; }
.hms-spinner { display: flex; justify-content: center; padding: 60px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-stat-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 16px; }
.hms-stat-num { font-size: 26px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 320px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input { background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 12px; color: var(--hms-h); font-size: 13px; outline: none; }
.hms-filter { width: auto; }
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; overflow: hidden; flex-shrink: 0; }
.hms-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; display: inline-flex; align-items: center; gap: 4px; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #b8860b; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-s); }
.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 30px; color: var(--hms-s); }
@media (max-width: 768px) { .hms-stat-row { grid-template-columns: repeat(2, 1fr); } }
</style>
